import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PERMISSIONS_STRUCTURE } from '@/utils/permissions';

export function PermissionsSelector({ selectedPermissions = [], onChange }) {
  const [expandedModules, setExpandedModules] = useState({});
  const [expandedSubmodules, setExpandedSubmodules] = useState({});

  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const toggleSubmodule = (moduleKey, submoduleKey) => {
    const key = `${moduleKey}.${submoduleKey}`;
    setExpandedSubmodules(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePermissionChange = (permissionId, checked) => {
    let newPermissions;
    if (checked) {
      newPermissions = [...selectedPermissions, permissionId];
    } else {
      newPermissions = selectedPermissions.filter(p => p !== permissionId);
    }
    onChange(newPermissions);
  };

  const handleSelectAllModule = (moduleKey, module) => {
    const modulePermissions = [];
    Object.entries(module.children).forEach(([submoduleKey, submodule]) => {
      submodule.permissions.forEach(perm => {
        modulePermissions.push(perm.id);
      });
    });

    const allSelected = modulePermissions.every(p => selectedPermissions.includes(p));
    
    let newPermissions;
    if (allSelected) {
      // Deseleccionar todos
      newPermissions = selectedPermissions.filter(p => !modulePermissions.includes(p));
    } else {
      // Seleccionar todos
      newPermissions = [...new Set([...selectedPermissions, ...modulePermissions])];
    }
    onChange(newPermissions);
  };

  const handleSelectAllSubmodule = (submodule) => {
    const submodulePermissions = submodule.permissions.map(p => p.id);
    const allSelected = submodulePermissions.every(p => selectedPermissions.includes(p));
    
    let newPermissions;
    if (allSelected) {
      newPermissions = selectedPermissions.filter(p => !submodulePermissions.includes(p));
    } else {
      newPermissions = [...new Set([...selectedPermissions, ...submodulePermissions])];
    }
    onChange(newPermissions);
  };

  const getModuleStats = (module) => {
    let total = 0;
    let selected = 0;
    
    Object.entries(module.children).forEach(([submoduleKey, submodule]) => {
      submodule.permissions.forEach(perm => {
        total++;
        if (selectedPermissions.includes(perm.id)) selected++;
      });
    });
    
    return { total, selected };
  };

  const getSubmoduleStats = (submodule) => {
    const total = submodule.permissions.length;
    const selected = submodule.permissions.filter(p => selectedPermissions.includes(p.id)).length;
    return { total, selected };
  };

  return (
    <div className="space-y-4">
      <div className="bg-white pb-2">
        <p className="text-sm text-muted-foreground">
          Selecciona los permisos que tendrá este usuario
        </p>
        <Badge variant="outline" className="mt-2">
          {selectedPermissions.length} permisos seleccionados
        </Badge>
      </div>
      
      <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 border rounded-lg p-3 bg-gray-50">

      {Object.entries(PERMISSIONS_STRUCTURE).map(([moduleKey, module]) => {
        const isExpanded = expandedModules[moduleKey];
        const stats = getModuleStats(module);
        const allSelected = stats.selected === stats.total && stats.total > 0;

        return (
          <Card key={moduleKey} className="border-2">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <button
                    onClick={() => toggleModule(moduleKey)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                  <CardTitle className="text-lg">{module.label}</CardTitle>
                  <Badge variant={stats.selected > 0 ? "default" : "outline"}>
                    {stats.selected}/{stats.total}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`module-${moduleKey}`}
                    checked={allSelected}
                    onCheckedChange={() => handleSelectAllModule(moduleKey, module)}
                  />
                  <Label htmlFor={`module-${moduleKey}`} className="text-sm font-medium cursor-pointer">
                    Seleccionar todos
                  </Label>
                </div>
              </div>
            </CardHeader>

            {isExpanded && (
              <CardContent className="space-y-3">
                {Object.entries(module.children).map(([submoduleKey, submodule]) => {
                  const subKey = `${moduleKey}.${submoduleKey}`;
                  const isSubExpanded = expandedSubmodules[subKey];
                  const subStats = getSubmoduleStats(submodule);
                  const subAllSelected = subStats.selected === subStats.total;

                  return (
                    <div key={subKey} className="border rounded-lg p-3 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 flex-1">
                          <button
                            onClick={() => toggleSubmodule(moduleKey, submoduleKey)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            {isSubExpanded ? (
                              <ChevronDown className="w-4 h-4" />
                            ) : (
                              <ChevronRight className="w-4 h-4" />
                            )}
                          </button>
                          <span className="font-medium text-sm">{submodule.label}</span>
                          <Badge variant="secondary" className="text-xs">
                            {subStats.selected}/{subStats.total}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`submodule-${subKey}`}
                            checked={subAllSelected}
                            onCheckedChange={() => handleSelectAllSubmodule(submodule)}
                          />
                          <Label htmlFor={`submodule-${subKey}`} className="text-xs cursor-pointer">
                            Todos
                          </Label>
                        </div>
                      </div>

                      {isSubExpanded && (
                        <div className="space-y-2 ml-6 mt-3">
                          {submodule.permissions.map((permission) => (
                            <div key={permission.id} className="flex items-center gap-2">
                              <Checkbox
                                id={permission.id}
                                checked={selectedPermissions.includes(permission.id)}
                                onCheckedChange={(checked) => handlePermissionChange(permission.id, checked)}
                              />
                              <Label
                                htmlFor={permission.id}
                                className="text-sm cursor-pointer hover:text-primary"
                              >
                                {permission.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>
        );
      })}
      </div>
    </div>
  );
}
